import React, { useState, useEffect } from 'react';
import { obtenerFechasPago, crearPagoDLocalGo, verificarEstadoPagoDLocalGo } from '../../services/pagosServices';
import { Lock, Shield, Loader, ExternalLink } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { obtenerMatriculaPorId, verificarHijos, actualizarMatricula } from '../../services/matriculaService';
import { obtenerCentro } from '../../services/centroService';
import 'react-toastify/dist/ReactToastify.css';



// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const Pagos = () => {
    const [error, setError] = useState('');
    const [isProcessingDLocal, setIsProcessingDLocal] = useState(false);
    const [matriculaId, setMatriculaId] = useState(null);
    const [fechasPago, setFechasPago] = useState(null);
    const [mostrarPago, setMostrarPago] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [amount, setAmount] = useState(0);
    const [pagoTarjetaPendiente, setPagoTarjetaPendiente] = useState(false);
    const [linkPago, setLinkPago] = useState(null);
    const [isVerificandoPago, setIsVerificandoPago] = useState(false);
    const [estadoPagoTarjeta, setEstadoPagoTarjeta] = useState('validando'); // 'validando' o 'pendiente'
    const [ultimoToastMostrado, setUltimoToastMostrado] = useState(null); // Para evitar toasts duplicados

    const verificarEstadoPago = async (idMatricula) => {
        try {
            const fechasResponse = await obtenerFechasPago(idMatricula);

            
            if (fechasResponse.success) {
                setFechasPago(fechasResponse.data);
                
                const fechaActual = new Date();
                fechaActual.setHours(0, 0, 0, 0);
                
                if (fechasResponse.data && fechasResponse.data.length > 0) {
                    const ultimoRegistro = fechasResponse.data[fechasResponse.data.length - 1];

                    // Si el último pago fue rechazado, mostrar opción de pago
                    if (ultimoRegistro.estado_verificacion === 'Rechazado') {
                        setLinkPago(null);
                        setMostrarPago(true);
                    } else if (ultimoRegistro.estado_verificacion === 'Pendiente') {
                        // Verificar si es una transferencia pendiente de verificación
                        const tieneTransferenciaPendiente = ultimoRegistro.tipo_pago === 'Transferencia' && 
                                                           ultimoRegistro.comprobante_pago;
                        
                        // Verificar si es un pago con tarjeta pendiente
                        const tienePagoTarjetaPendiente = ultimoRegistro.tipo_pago === 'Tarjeta';
                        
                        if (tieneTransferenciaPendiente) {
                            setMostrarPago(false); // No mostrar pago si hay transferencia en revisión
                            setPagoTarjetaPendiente(false);
                        } else if (tienePagoTarjetaPendiente) {
                            setMostrarPago(false); // No mostrar pago si hay tarjeta en validación
                            setPagoTarjetaPendiente(true);
                            setEstadoPagoTarjeta('validando'); // Inicialmente en validación
                            // Obtener el link de pago desde comprobante_pago
                            setLinkPago(ultimoRegistro.comprobante_pago || null);
                            
                            // Verificar automáticamente el estado del pago si existe payment_id
                            if (ultimoRegistro.payment_id && !isVerificandoPago) {
                                verificarEstadoPagoTarjeta(ultimoRegistro.payment_id);
                            }
                        } else {
                            // Estado pendiente sin transferencia ni tarjeta - verificar fecha
                            if (ultimoRegistro.proxima_fecha_pago) {
                                const proximaFechaPago = new Date(ultimoRegistro.proxima_fecha_pago);
                                proximaFechaPago.setHours(0, 0, 0, 0);
                                const mostrarPagoCalculado = fechaActual >= proximaFechaPago;
                                setMostrarPago(mostrarPagoCalculado);
                            } else {
                                setMostrarPago(true); // Si no hay fecha, permitir pago
                            }
                            setPagoTarjetaPendiente(false);
                        }
                    } else if (ultimoRegistro.estado_verificacion === 'Verificado') {
                        // Pago verificado - limpiar link de pago y verificar si necesita próximo pago
                        setLinkPago(null);
                        if (ultimoRegistro.proxima_fecha_pago) {
                            const proximaFechaPago = new Date(ultimoRegistro.proxima_fecha_pago);
                            proximaFechaPago.setHours(0, 0, 0, 0);
                            const mostrarPagoCalculado = fechaActual >= proximaFechaPago;
                            setMostrarPago(mostrarPagoCalculado);
                        } else {
                            setMostrarPago(false); // Si no hay próxima fecha, no requiere pago
                        }
                        setPagoTarjetaPendiente(false);
                    } else {
                        console.warn('No se encontró próxima fecha de pago en el último registro');
                        setMostrarPago(true); // Si no hay fecha, permitir pago
                        setPagoTarjetaPendiente(false);
                    }
                } else {
                    console.warn('No se encontraron registros de control de pagos para matrícula:', idMatricula);
                    setMostrarPago(true); // Si no hay control de pagos, permitir pago
                    setPagoTarjetaPendiente(false);
                }
            } else {
                console.warn('Error en la respuesta de fechas de pago:', fechasResponse);
                setMostrarPago(true); // En caso de error, permitir pago
                setPagoTarjetaPendiente(false);
            }
        } catch (err) {
            console.error('Error al verificar estado de pago:', err);
            setMostrarPago(true); // En caso de error, permitir pago
            setPagoTarjetaPendiente(false);
        } finally {
            setIsLoading(false);
        }
    };

    const verificarEstadoPagoTarjeta = async (payment_id) => {
        // Evitar múltiples peticiones simultáneas
        if (isVerificandoPago) {
            console.log('Ya se está verificando el pago, esperando...');
            return;
        }
        
        try {
            setIsVerificandoPago(true);
            console.log('Verificando estado del pago con payment_id:', payment_id);
            const response = await verificarEstadoPagoDLocalGo(payment_id);
            
            if (response.success && response.payment) {
                console.log('Estado del pago:', response.payment.status);
                console.log('Respuesta completa:', response);
                
                // Verificar si el pago está completado
                if (response.payment.status === 'PAID') {
                    console.log('¡Pago completado! El pago ha sido aprobado.');
                    showSuccessToast('¡Pago verificado exitosamente!');
                    // Recargar la página para actualizar el estado
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else if (response.payment.status === 'PENDING') {
                    console.log('Pago aún pendiente de procesamiento.');
                    setEstadoPagoTarjeta('pendiente');
                    
                    // Evitar mostrar el mismo toast múltiples veces
                    const ahora = Date.now();
                    if (!ultimoToastMostrado || (ahora - ultimoToastMostrado) > 5000) { // 5 segundos de diferencia
                        showWarningToast('El pago está pendiente de procesamiento o aún no lo ha realizado');
                        setUltimoToastMostrado(ahora);
                    }
                } else {
                    console.log('Estado del pago:', response.payment.status);
                }
            }
        } catch (error) {
            console.error('Error al verificar estado del pago:', error);
        } finally {
            setIsVerificandoPago(false);
        }
    };

    useEffect(() => {
        const obtenerDatos = async () => {
            try {
                // Verificar si el usuario regresó de dLocal Go
                const urlParams = new URLSearchParams(window.location.search);
                const fromDLocal = urlParams.get('from_dlocal') || sessionStorage.getItem('returning_from_dlocal');
                
                // Obtener datos de matrícula y plan
                const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
                if (hijoSeleccionado && hijoSeleccionado.id_child) {
                    const response = await obtenerMatriculaPorId(hijoSeleccionado.id_child);
                    const idMatricula = response.matricula.matricula.id_matricula;
                    setMatriculaId(idMatricula);
                    
                    // Obtener precio del plan seleccionado desde localStorage
                    const planSeleccionado = JSON.parse(localStorage.getItem('planSeleccionado') || 'null');
                    
                    if (planSeleccionado && planSeleccionado.monto) {
                        const precio = Number(planSeleccionado.monto);
                        setAmount(isNaN(precio) ? 0 : precio);
                    } else {
                        // Fallback: obtener precio del plan desde la información del hijo
                        const user = JSON.parse(localStorage.getItem('user'));
                        const userId = user?.id_usuario || user?.id;
                        
                        if (user && userId) {
                            try {
                                const hijosResponse = await verificarHijos(userId);
                                const hijosActivos = hijosResponse.hijos?.filter(hijo => hijo.estado_matricula === 'Activa') || [];
                                const hijo = hijosActivos.find(h => h.id_matricula === parseInt(idMatricula));
                                
                                if (hijo && hijo.plan_monto) {
                                    const precio = Number(hijo.plan_monto);
                                    setAmount(isNaN(precio) ? 0 : precio);
                                } else {
                                    console.warn('No se pudo obtener el precio del plan para la matrícula:', idMatricula);
                                    setAmount(0);
                                }
                            } catch (err) {
                                console.error('Error al obtener precio del plan:', err);
                                setAmount(0);
                            }
                        }
                    }
                    
                    await verificarEstadoPago(idMatricula);
                    
                    // Si regresó de dLocal Go, verificar automáticamente el estado del pago
                    if (fromDLocal) {
                        console.log('Usuario regresó de dLocal Go, verificando estado del pago...');
                        sessionStorage.removeItem('returning_from_dlocal');
                        // Limpiar la URL
                        if (urlParams.get('from_dlocal')) {
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }
                        // Esperar un momento para que se carguen los datos y luego verificar
                        setTimeout(async () => {
                            const fechasResponse = await obtenerFechasPago(idMatricula);
                            if (fechasResponse.success && fechasResponse.data && fechasResponse.data.length > 0) {
                                const ultimoRegistro = fechasResponse.data[fechasResponse.data.length - 1];
                                if (ultimoRegistro.payment_id && ultimoRegistro.tipo_pago === 'Tarjeta') {
                                    console.log('Verificando automáticamente el pago después del regreso de dLocal Go');
                                    verificarEstadoPagoTarjeta(ultimoRegistro.payment_id);
                                }
                            }
                        }, 2000);
                    }
                }
            } catch (err) {
                console.error('Error al obtener datos:', err);
                setError('Error al obtener información necesaria');
                setIsLoading(false);
            }
        };

        obtenerDatos();
    }, []);



    const handleDLocalGoPago = async () => {
        setError('');
        setIsProcessingDLocal(true);

        try {
            const response = await crearPagoDLocalGo(matriculaId);

            if (response.success && response.redirect_url) {
                // Marcar que el usuario está yendo a dLocal Go para detectar el regreso
                sessionStorage.setItem('returning_from_dlocal', 'true');
                // Redirigir directamente en la misma ventana sin mostrar mensaje de éxito
                // dLocal Go se encargará de redirigir de vuelta al sistema después del pago
                window.location.href = response.redirect_url;
            } else {
                setError('Error al crear el pago con dLocal Go');
            }
        } catch (err) {
            setError(err.message);
            showErrorToast(err.message);
        } finally {
            setIsProcessingDLocal(false);
        }
    };



    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
                <p className="text-gray-600">Verificando estado de pagos...</p>
            </div>
        );
    }

    // Mostrar mensaje de validación si hay pago con tarjeta pendiente
    if (pagoTarjetaPendiente) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center p-12 backdrop-blur-sm bg-white/40 rounded-3xl shadow-sm border border-yellow-100/50 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center space-y-6">
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                            <Loader className="w-8 h-8 text-yellow-600 animate-spin" />
                        </div>
                        <h2 className="text-2xl font-light text-gray-800">
                            {estadoPagoTarjeta === 'pendiente' ? 'Pago Pendiente' : 'Validando tu pago'}
                        </h2>
                        <p className="text-gray-500 font-light">
                            {estadoPagoTarjeta === 'pendiente' 
                                ? 'Tu pago con tarjeta está pendiente de procesamiento' 
                                : 'Tu pago con tarjeta está siendo procesado y validado'
                            }
                        </p>
                        <div className="bg-yellow-50/50 border border-yellow-200 rounded-lg p-4 max-w-sm">
                            <p className="text-sm text-yellow-700">
                                <strong>Estado:</strong> {estadoPagoTarjeta === 'pendiente' ? 'Pendiente' : 'En validación'}<br/>
                                <strong>Método:</strong> Tarjeta (dLocal Go)
                            </p>
                        </div>
                        <div className="mt-4 flex flex-col gap-3">
                            {linkPago && (
                                <button
                                    onClick={() => window.open(linkPago, '_blank')}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm justify-center"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Acceder al Link de Pago
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    const ultimoRegistro = fechasPago && fechasPago.length > 0 ? fechasPago[fechasPago.length - 1] : null;
                                    if (ultimoRegistro && ultimoRegistro.payment_id && !isVerificandoPago) {
                                        verificarEstadoPagoTarjeta(ultimoRegistro.payment_id);
                                    }
                                }}
                                disabled={isVerificandoPago}
                                className={`${isVerificandoPago ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm justify-center`}
                            >
                                {isVerificandoPago ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Shield className="w-4 h-4" />
                                )}
                                {isVerificandoPago ? 'Verificando...' : 'Verificar Estado del Pago'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!mostrarPago) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center p-12 backdrop-blur-sm bg-white/40 rounded-3xl shadow-sm border border-green-100/50 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center space-y-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-light text-gray-800">¡Estás al día con tus pagos!</h2>
                        <p className="text-gray-500 font-light">No hay pagos pendientes en este momento</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container mx-auto px-3 sm:px-6 lg:px-8">
                <ToastContainer 
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                    className="mt-16"
                    style={{ zIndex: 9999 }}
                />
                <div className="min-h-screen flex items-center justify-center p-2 sm:p-4">
                    <div className="w-full max-w-md space-y-4 sm:space-y-6">
                        <div className="text-center space-y-2">
                            <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Pago de mensualidad</h2>
                            <p className="text-sm sm:text-base text-gray-500">Asegura el bienestar y desarrollo de tu hijo</p>
                        </div>

                        <div className="backdrop-blur-md bg-white/30 rounded-xl sm:rounded-2xl shadow-xl border border-white/50 p-4 sm:p-8">
                            <div className="mb-4 sm:mb-8 text-center">
                                <div className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">
                                    ${amount.toFixed(2)} <span className="text-base sm:text-lg font-normal text-gray-500">USD/mes</span>
                                </div>
                                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-600">
                                    <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span>Pago seguro con dLocal Go</span>
                                </div>
                            </div>

                            <div className="space-y-4 sm:space-y-6">
                                {error && (
                                    <div className="bg-red-50/50 border border-red-100 text-red-600 p-3 sm:p-4 rounded-lg sm:rounded-xl flex items-center gap-2 text-xs sm:text-sm">
                                        <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={handleDLocalGoPago}
                                    disabled={isProcessingDLocal || !matriculaId}
                                    className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base font-medium"
                                >
                                    <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                                    {isProcessingDLocal ? 'Creando pago...' : `Pagar $${amount.toFixed(2)} USD`}
                                </button>
                            </div>

                            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200/50">
                                <div className="flex flex-col items-center gap-4 sm:gap-6">
                                    <div className="flex items-center gap-4 sm:gap-8">
                                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600 text-xs sm:text-sm">
                                            <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                                            <span className="font-medium">SSL Seguro</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-600 text-xs sm:text-sm">
                                            <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                                            <span className="font-medium">Datos Protegidos</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-400 text-xs sm:text-sm">
                                        <span>Powered by dLocal Go</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Pagos;